import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { httpsCallable, functions } from "./firebase.js";
import { isDemoBuild } from "./siteConfig.js";
import { resolveAssessorLinks } from "./parcelGis.js";

function FitBounds({ geojson }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson) return;
    const layer = L.geoJSON(geojson);
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: showCommunityMaxZoom(geojson) });
    }
  }, [geojson, map]);
  return null;
}

function showCommunityMaxZoom(geojson) {
  const count = geojson?.features?.length || 0;
  return count > 1 ? 17 : 18;
}

function ParcelBoundaryMap({
  parcelGis,
  address,
  className = "",
  showCommunity = false,
  highlightPin: highlightPinProp,
}) {
  const [geojson, setGeojson] = useState(null);
  const [parcelCount, setParcelCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const pin = parcelGis?.pin;
  const highlightPin = highlightPinProp || pin;
  const centroid = parcelGis?.mapLinks?.centroid || parcelGis?.centroid;
  const assessorLinks = resolveAssessorLinks(parcelGis);

  useEffect(() => {
    if (showCommunity) {
      if (isDemoBuild) {
        if (centroid) {
          const delta = 0.00035;
          const offsets = [
            [0, 0],
            [0.0008, 0.0004],
            [-0.0007, 0.0005],
            [0.0005, -0.0008],
          ];
          setGeojson({
            type: "FeatureCollection",
            features: offsets.map(([lngOff, latOff], index) => ({
              type: "Feature",
              properties: { PIN: index === 0 ? highlightPin : `DEMO-${index}` },
              geometry: {
                type: "Polygon",
                coordinates: [
                  [
                    [centroid.lng + lngOff - delta, centroid.lat + latOff - delta],
                    [centroid.lng + lngOff + delta, centroid.lat + latOff - delta],
                    [centroid.lng + lngOff + delta, centroid.lat + latOff + delta],
                    [centroid.lng + lngOff - delta, centroid.lat + latOff + delta],
                    [centroid.lng + lngOff - delta, centroid.lat + latOff - delta],
                  ],
                ],
              },
            })),
          });
          setParcelCount(offsets.length);
        }
        return undefined;
      }

      let cancelled = false;
      setLoading(true);
      setError(null);
      (async () => {
        try {
          const getCommunityParcelBoundaries = httpsCallable(
            functions,
            "getCommunityParcelBoundaries",
          );
          const result = await getCommunityParcelBoundaries({ highlightPin });
          if (!cancelled) {
            setGeojson(result.data?.geojson || null);
            setParcelCount(result.data?.parcelCount || 0);
          }
        } catch (err) {
          if (!cancelled) setError(err.message || "Could not load community parcel boundaries");
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (!pin && !address) return undefined;
    if (isDemoBuild) {
      if (centroid) {
        const delta = 0.00035;
        setGeojson({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: { PIN: pin },
              geometry: {
                type: "Polygon",
                coordinates: [
                  [
                    [centroid.lng - delta, centroid.lat - delta],
                    [centroid.lng + delta, centroid.lat - delta],
                    [centroid.lng + delta, centroid.lat + delta],
                    [centroid.lng - delta, centroid.lat + delta],
                    [centroid.lng - delta, centroid.lat - delta],
                  ],
                ],
              },
            },
          ],
        });
        setParcelCount(1);
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const getParcelBoundary = httpsCallable(functions, "getParcelBoundary");
        const result = await getParcelBoundary({ pin, address });
        if (!cancelled) {
          setGeojson(result.data?.geojson || null);
          setParcelCount(1);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not load parcel boundary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pin, address, centroid, highlightPin, showCommunity]);

  const mapCenter = useMemo(() => {
    if (centroid) return [centroid.lat, centroid.lng];
    return [35.248242, -83.69559];
  }, [centroid]);

  const parcelStyle = useCallback(
    (feature) => {
      const featurePin = feature?.properties?.PIN;
      const isHighlight = !showCommunity || featurePin === highlightPin;
      return {
        color: isHighlight ? "#1d4ed8" : "#64748b",
        weight: isHighlight ? 3 : 1.5,
        fillColor: isHighlight ? "#3b82f6" : "#94a3b8",
        fillOpacity: isHighlight ? 0.22 : 0.06,
      };
    },
    [showCommunity, highlightPin],
  );

  const mapHeight = showCommunity ? 360 : 260;

  return (
    <div className={`parcel-boundary-map-wrap ${className}`.trim()}>
      {loading ? (
        <p className="parcel-boundary-status">
          {showCommunity ? "Loading community property lines…" : "Loading property lines…"}
        </p>
      ) : null}
      {error ? <p className="parcel-boundary-status parcel-boundary-error">{error}</p> : null}
      <MapContainer
        center={mapCenter}
        zoom={showCommunity ? 15 : 16}
        scrollWheelZoom={false}
        className={`parcel-boundary-map${showCommunity ? " parcel-boundary-map-community" : ""}`}
        style={{ height: mapHeight, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geojson ? <GeoJSON data={geojson} style={parcelStyle} /> : null}
        {geojson ? <FitBounds geojson={geojson} /> : null}
      </MapContainer>
      <p className="parcel-boundary-caption">
        {showCommunity ? (
          <>
            {parcelCount > 0 ? `${parcelCount} community lots` : "Community lots"} from{" "}
            {parcelGis?.dataSource || "county tax GIS"}. Your lot is highlighted in blue.
          </>
        ) : (
          <>
            Blue outline from {parcelGis?.dataSource || "county tax GIS"}.
          </>
        )}
        {assessorLinks?.countyMap ? (
          <>
            {" "}
            <a href={assessorLinks.countyMap}>Open county map for this PIN</a>
          </>
        ) : null}
      </p>
    </div>
  );
}

export default ParcelBoundaryMap;